/* =============================================================================
   T3BLOGG_OS — assets/folder-content.js
   Behaviors for the video-folder and image-folder sections:
     - Click a .video-card → swap that window's body with the YouTube embed.
     - Click an .image-tile → open a fullscreen lightbox.
   ============================================================================= */

(function () {
  'use strict';

  function escHTML(s) {
    return String(s || '').replace(/[&<>"']/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  // ---- VIDEO ---------------------------------------------------------------
  document.addEventListener('click', e => {
    const card = e.target.closest('.video-card');
    if (!card) return;
    e.preventDefault();

    const win = card.closest('.win');
    if (!win) return;
    const body = win.querySelector(':scope > .body');
    if (!body) return;

    const vid = card.dataset.vid;
    if (!vid) return;

    // Cache the original list HTML so we can return to it.
    if (!body.dataset.listSnapshot) body.dataset.listSnapshot = body.innerHTML;

    const title   = card.dataset.title   || '';
    const channel = card.dataset.channel || '';
    const rec     = card.dataset.rec     || '';
    const loc     = card.dataset.loc     || '';
    const dir     = card.dataset.dir     || '';
    const dur     = card.dataset.duration|| '';
    const desc    = card.dataset.desc    || '';

    body.innerHTML = `
      <div style="padding:14px">
        <button type="button" class="video-back">&lt; BACK TO LIST</button>
        <div class="video-player">
          <iframe src="https://www.youtube-nocookie.com/embed/${encodeURIComponent(vid)}?autoplay=1"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen></iframe>
        </div>
        <div class="video-meta-large">
          <div class="vtitle">${escHTML(title)}</div>
          <div class="row">
            ${channel ? `<span>CHANNEL <b>${escHTML(channel)}</b></span>` : ''}
            ${dur     ? `<span>DURATION <b>${escHTML(dur)}</b></span>` : ''}
          </div>
          <div class="row">
            ${rec ? `<span>REC <b>${escHTML(rec)}</b></span>` : ''}
            ${loc ? `<span>LOC <b>${escHTML(loc)}</b></span>` : ''}
            ${dir ? `<span>DIR <b>${escHTML(dir)}</b></span>` : ''}
          </div>
          ${desc ? `<p style="color:var(--phosphor-dim);font-size:11px;line-height:1.6;margin-top:8px">${escHTML(desc)}</p>` : ''}
        </div>
      </div>
    `;

    body.querySelector('.video-back').addEventListener('click', () => {
      const snap = body.dataset.listSnapshot;
      if (snap) body.innerHTML = snap;
    });

    document.dispatchEvent(new CustomEvent('syslog', { detail: { text: `PLAYING youtube:${vid}` } }));
  });

  // ---- IMAGE LIGHTBOX (gallery: prev/next, arrow keys, swipe) -------------
  let lb = null;   // { tiles, index, el, imgEl, capEl }

  function lbSrc(i) {
    const n = lb.tiles.length;
    const t = lb.tiles[((i % n) + n) % n];
    return t ? (t.dataset.src || (t.querySelector('img') || {}).src || '') : '';
  }

  // Warm the browser cache for nearby images so next/prev switches instantly.
  function lbPreload(i) {
    const src = lbSrc(i);
    if (!src) return;
    const im = new Image();
    im.src = src;
    lb._pre.push(im);
    while (lb._pre.length > 8) lb._pre.shift();
  }

  function lbRender() {
    if (!lb) return;
    const caption = lb.tiles[lb.index].dataset.caption || '';
    lb.imgEl.src = lbSrc(lb.index);
    lb.imgEl.alt = caption;
    if (lb.capEl) {
      lb.capEl.textContent = caption;
      lb.capEl.style.display = caption ? '' : 'none';
    }
    const multi = lb.tiles.length > 1;
    lb.el.querySelectorAll('.lb-nav').forEach(b => { b.style.display = multi ? '' : 'none'; });
    if (multi) { lbPreload(lb.index + 1); lbPreload(lb.index - 1); lbPreload(lb.index + 2); }
  }

  function lbGo(delta) {
    if (!lb) return;
    const n = lb.tiles.length;
    lb.index = (lb.index + delta + n) % n;
    lbRender();
  }

  function closeLightbox() {
    if (lb) { lb.el.remove(); lb = null; }
    document.removeEventListener('keydown', onLightboxKey);
  }
  function onLightboxKey(e) {
    if (!lb) return;
    if (e.key === 'Escape') closeLightbox();
    else if (e.key === 'ArrowRight') lbGo(1);
    else if (e.key === 'ArrowLeft') lbGo(-1);
  }

  document.addEventListener('click', e => {
    const tile = e.target.closest('.image-tile');
    if (!tile) return;
    e.preventDefault();

    const grid = tile.closest('.image-grid') || document;
    const tiles = Array.from(grid.querySelectorAll('.image-tile'));
    const index = tiles.indexOf(tile);
    if (index < 0) return;

    const el = document.createElement('div');
    el.className = 'lightbox';
    el.innerHTML = `
      <button type="button" class="close" aria-label="close">×</button>
      <button type="button" class="lb-nav lb-prev" aria-label="previous image">&#8249;</button>
      <img alt="">
      <button type="button" class="lb-nav lb-next" aria-label="next image">&#8250;</button>
      <span class="caption"></span>
    `;
    document.body.appendChild(el);
    lb = { tiles, index, el, imgEl: el.querySelector('img'), capEl: el.querySelector('.caption'), _pre: [] };
    lbRender();

    el.addEventListener('click', ev => {
      if (ev.target.closest('.lb-next')) { lbGo(1); return; }
      if (ev.target.closest('.lb-prev')) { lbGo(-1); return; }
      if (ev.target.closest('.close')) { closeLightbox(); return; }
      if (ev.target.tagName === 'IMG') return;   // clicking the image doesn't close
      closeLightbox();                            // backdrop click closes
    });

    // Swipe (mobile): horizontal drag past threshold flips image.
    let sx = 0, sy = 0;
    el.addEventListener('touchstart', ev => {
      const t = ev.changedTouches[0]; sx = t.clientX; sy = t.clientY;
    }, { passive: true });
    el.addEventListener('touchend', ev => {
      const t = ev.changedTouches[0];
      const dx = t.clientX - sx, dy = t.clientY - sy;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) lbGo(dx < 0 ? 1 : -1);
    }, { passive: true });

    document.addEventListener('keydown', onLightboxKey);
    document.dispatchEvent(new CustomEvent('syslog', { detail: { text: 'OPENED image // lightbox' } }));
  });
})();
